import React, { useEffect, useState } from 'react';
import BottomSheet from './ui/BottomSheet';
import Messages from './Dispatch/LiveDispatch/Messages';

/**
 * Contact Dispatch — three ways to reach dispatch, in order of preference:
 * Chat, Call the Driver Help Line (tel: link, fleet-configured number), and
 * Request a Callback.
 *
 * Chat has two possible backends depending on dispatch type, and both give a
 * real, visible sent-message bubble — never a silent side-channel:
 *  - itinerary / shuttlebus-*: the route-scoped ChatDialog (via
 *    onOpenChat(message), which App.js wires to open it with a pending
 *    message it auto-sends).
 *  - live / demandresponse / nightowl: these have no routeId at all, so
 *    ChatDialog can never load for them. They fall back to the same
 *    vehicle-keyed Messages modal LiveDispatch already uses for its own
 *    in-map "Messages" button — NOT a redirect to the Dispatch screen.
 *
 * "Request a callback" pre-fills and auto-sends into whichever of the two is
 * active, so the driver sees the same confirmation either way.
 */
function ContactDispatchSheet({
  open,
  onClose,
  canOpenChat,
  onOpenChat,
  driverName,
  driverPhone,
  vehicleNumber,
  helpLinePhone,
}) {
  const [liveMessagesOpen, setLiveMessagesOpen] = useState(false);
  const [pendingLiveMessage, setPendingLiveMessage] = useState(null);

  // Reset on fresh open only — NOT on close. The fallback path below closes
  // this sheet (via onClose()) in the same action that opens the Messages
  // modal, so resetting on `!open` would immediately close the modal it just
  // opened.
  useEffect(() => {
    if (open) {
      setLiveMessagesOpen(false);
      setPendingLiveMessage(null);
    }
  }, [open]);

  const callMeMessage = driverPhone
    ? `☎️ Call me — ${driverName || 'driver'}, ${driverPhone}`
    : `☎️ Callback requested by ${driverName || 'driver'} — number not on file, please look up.`;

  const handleChat = () => {
    onClose();
    if (canOpenChat) {
      onOpenChat();
    } else {
      setLiveMessagesOpen(true);
    }
  };

  const handleCallback = () => {
    onClose();
    if (canOpenChat) {
      // Opens ChatDialog, which auto-sends this text and shows it as a normal
      // sent-message bubble once history has loaded.
      onOpenChat(callMeMessage);
    } else {
      setPendingLiveMessage(callMeMessage);
      setLiveMessagesOpen(true);
    }
  };

  const tileClass =
    'flex w-full items-center gap-3 rounded-2xl bg-gray-50 px-4 py-3.5 text-left active:scale-[0.98] transition-transform';

  return (
    <>
      <BottomSheet open={open} onClose={onClose} title="Contact Dispatch">
        <div className="flex flex-col gap-3 py-1">
          <button onClick={handleChat} className={tileClass}>
            <span className="text-2xl">💬</span>
            <span>
              <span className="block text-sm font-semibold text-gray-900">Chat with Dispatch</span>
              <span className="block text-xs text-gray-500">Preferred — fastest response</span>
            </span>
          </button>

          {helpLinePhone ? (
            <a href={`tel:${helpLinePhone}`} className={tileClass}>
              <span className="text-2xl">📞</span>
              <span>
                <span className="block text-sm font-semibold text-gray-900">Call Driver Help Line</span>
                <span className="block text-xs text-gray-500">{helpLinePhone}</span>
              </span>
            </a>
          ) : (
            <div className={`${tileClass} opacity-50`}>
              <span className="text-2xl">📞</span>
              <span>
                <span className="block text-sm font-semibold text-gray-900">Call Driver Help Line</span>
                <span className="block text-xs text-gray-500">Not configured for this fleet</span>
              </span>
            </div>
          )}

          <button onClick={handleCallback} className={tileClass}>
            <span className="text-2xl">↩️</span>
            <span>
              <span className="block text-sm font-semibold text-gray-900">Request a callback</span>
              <span className="block text-xs text-gray-500">
                {driverPhone
                  ? `Sends "call me" with your number (${driverPhone}) in chat`
                  : 'Sends a "call me" message in chat'}
              </span>
            </span>
          </button>
        </div>
      </BottomSheet>

      {/* Fallback chat for dispatch types with no routeId (Live/DemandResponse/
          NightOwl) — the same modal LiveDispatch's own "Messages" button uses. */}
      {!canOpenChat && (
        <Messages
          isOpen={liveMessagesOpen}
          onClose={() => { setLiveMessagesOpen(false); setPendingLiveMessage(null); }}
          vehicleNumber={vehicleNumber}
          driverName={driverName}
          pendingMessage={pendingLiveMessage}
          onPendingMessageSent={() => setPendingLiveMessage(null)}
        />
      )}
    </>
  );
}

export default ContactDispatchSheet;
